import { useState, useEffect } from "react";
import Header from "../../../components/layout/Header";
import Sidebar from "../../../components/layout/Sidebar";
import CountUp from "react-countup";
import FeatherIcon from "feather-icons-react/build/FeatherIcon";
import { Link } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { surgeryService, STATUS_LABELS, type SurgeonDashboardStats, type SurgicalProcedureWithRequirements } from "../surgeryService";
import Breadcrumb from '../../../components/ui/Breadcrumb';
import Spinner from '../../../components/ui/Spinner';
import { parseApiDate } from '../../../lib/format';

const ACCEPTANCE_BADGE: Record<string, string> = {
  ready: "custom-badge status-green",
  attention: "custom-badge status-orange",
  urgent: "custom-badge status-pink",
  no_requirements: "custom-badge status-purple",
};

const ACCEPTANCE_LABELS: Record<string, string> = {
  ready: "Listo",
  attention: "Atención",
  urgent: "Urgente",
  no_requirements: "Sin requisitos",
};

const getWeekRange = () => {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(now.setDate(diff));
  // Date arithmetic on a Date we just built, not an API value — a plain
  // copy is what is wanted here.
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return {
    from: monday.toISOString().slice(0, 10),
    to: sunday.toISOString().slice(0, 10),
  };
};

const SurgeonDashboard = () => {
  const { user, isAdmin } = useAuth();
  const doctorId = user?.doctor?.id ?? null;

  const [stats, setStats] = useState<SurgeonDashboardStats | null>(null);
  const [procedures, setProcedures] = useState<SurgicalProcedureWithRequirements[]>([]);
  const [loading, setLoading] = useState(true);

  const weekRange = getWeekRange();
  const [filters, setFilters] = useState({
    date_from: weekRange.from,
    date_to: weekRange.to,
    status: "",
    doctor_id: "",
  });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const params: Record<string, string | number> = {
          from: filters.date_from,
          to: filters.date_to,
        };
        if (filters.status) params.status = filters.status;
        if (filters.doctor_id) params.doctor_id = Number(filters.doctor_id);
        if (!params.doctor_id && doctorId) params.doctor_id = doctorId;

        const response = await surgeryService.getSurgeonDashboard(params);
        setStats(response.stats);
        setProcedures(response.data);
      } catch (error) {
        console.error("Error loading surgeon dashboard:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [filters, doctorId]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    return (parseApiDate(dateStr) ?? new Date(NaN)).toLocaleDateString("es-DO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (dateStr: string) => {
    if (!dateStr) return "-";
    return (parseApiDate(dateStr) ?? new Date(NaN)).toLocaleTimeString("es-DO", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const surgeonFullName = user?.doctor
    ? `${user.doctor.first_name} ${user.doctor.last_name}`
    : user?.name ?? "";

  const specialty = user?.doctor?.specialty?.name ?? null;

  return (
    <>
      <Header />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Panel Quirúrgico' }]} />
              </div>
            </div>
          </div>

          <div className="good-morning-blk">
            <div className="row">
              <div className="col-md-6">
                <div className="morning-user">
                  <h2>
                    Panel Quirúrgico, <span>{surgeonFullName}</span>
                  </h2>
                  <p>
                    {specialty
                      ? `${specialty} — Resumen de preparación pre-operatoria`
                      : "Resumen de preparación pre-operatoria"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="doctor-list-blk">
            <div className="row">
              <div className="col-xl-3 col-md-6">
                <div className="doctor-widget">
                  <div className="doctor-box-icon flex-shrink-0" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className="fas fa-calendar-week" style={{ fontSize: 32, color: "#0d4cd3" }} />
                  </div>
                  <div className="doctor-content dash-count flex-grow-1">
                    <h4>
                      <CountUp delay={0.4} end={stats?.total_this_week ?? 0} duration={0.6} />
                    </h4>
                    <h5>Cirugías esta semana</h5>
                  </div>
                </div>
              </div>

              <div className="col-xl-3 col-md-6">
                <div className="doctor-widget">
                  <div className="doctor-box-icon flex-shrink-0" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className="fas fa-check-circle" style={{ fontSize: 32, color: "#28a745" }} />
                  </div>
                  <div className="doctor-content dash-count flex-grow-1">
                    <h4>
                      <CountUp delay={0.4} end={stats?.ready_count ?? 0} duration={0.6} />
                    </h4>
                    <h5>Pacientes Listos</h5>
                  </div>
                </div>
              </div>

              <div className="col-xl-3 col-md-6">
                <div className="doctor-widget">
                  <div className="doctor-box-icon flex-shrink-0" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className="fas fa-exclamation-triangle" style={{ fontSize: 32, color: "#f39c12" }} />
                  </div>
                  <div className="doctor-content dash-count flex-grow-1">
                    <h4>
                      <CountUp delay={0.4} end={stats?.attention_count ?? 0} duration={0.6} />
                    </h4>
                    <h5>Atención</h5>
                  </div>
                </div>
              </div>

              <div className="col-xl-3 col-md-6">
                <div className="doctor-widget">
                  <div className="doctor-box-icon flex-shrink-0" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className="fas fa-exclamation-circle" style={{ fontSize: 32, color: "#dc3545" }} />
                  </div>
                  <div className="doctor-content dash-count flex-grow-1">
                    <h4>
                      <CountUp delay={0.4} end={stats?.urgent_count ?? 0} duration={0.6} />
                    </h4>
                    <h5>Urgentes</h5>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {stats?.completion_rate != null && (
            <div className="row mb-3">
              <div className="col-12">
                <div className="card">
                  <div className="card-body py-2">
                    <div className="d-flex align-items-center gap-3">
                      <span style={{ fontWeight: 500 }}>Tasa de Completitud:</span>
                      <div className="progress flex-grow-1" style={{ height: 18 }}>
                        <div
                          className="progress-bar bg-success"
                          role="progressbar"
                          style={{ width: `${stats.completion_rate}%` }}
                        >
                          {stats.completion_rate}%
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h4 className="card-title d-inline-block">Próximas Cirugías</h4>
                </div>
                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-md-3">
                      <label className="form-label">Desde</label>
                      <input
                        type="date"
                        className="form-control"
                        value={filters.date_from}
                        onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Hasta</label>
                      <input
                        type="date"
                        className="form-control"
                        value={filters.date_to}
                        onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Estado</label>
                      <select
                        className="form-select"
                        value={filters.status}
                        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                      >
                        <option value="">Todos los estados</option>
                        <option value="scheduled">Programada</option>
                        <option value="completed">Completada</option>
                        <option value="cancelled">Cancelada</option>
                      </select>
                    </div>
                    {isAdmin() && (
                      <div className="col-md-3">
                        <label className="form-label">Médico (ID)</label>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="ID del médico"
                          value={filters.doctor_id}
                          onChange={(e) => setFilters({ ...filters, doctor_id: e.target.value })}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="card-body p-0 table-dash">
                  <div className="table-responsive">
                    <table className="table mb-0 border-0 custom-table">
                      <thead>
                        <tr>
                          <th>Paciente</th>
                          <th>Procedimiento</th>
                          <th>Fecha</th>
                          <th>Hora</th>
                          <th>Requisitos</th>
                          <th>Estado</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan={7} className="text-center py-3">
                              <Spinner />
                            </td>
                          </tr>
                        ) : procedures.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="text-center text-muted py-3">
                              No hay cirugías programadas para este período
                            </td>
                          </tr>
                        ) : (
                          procedures.map((proc) => {
                            const summary = proc.requirements_summary || { completed: 0, pending: 0, waived: 0, total: 0 };
                            const status = proc.acceptance_status || "no_requirements";
                            const progressPct = summary.total > 0
                              ? Math.round((summary.completed / summary.total) * 100)
                              : 0;
                            return (
                              <tr key={proc.id}>
                                <td>
                                  {proc.patient
                                    ? `${proc.patient.first_name} ${proc.patient.last_name}`
                                    : "—"}
                                </td>
                                <td>{proc.procedure_name}</td>
                                <td>{formatDate(proc.procedure_date)}</td>
                                <td>{formatTime(proc.procedure_date)}</td>
                                <td>
                                  <div className="d-flex align-items-center gap-2">
                                    <div className="progress flex-grow-1" style={{ height: 14, minWidth: 80 }}>
                                      <div
                                        className={`progress-bar ${progressPct >= 80 ? "bg-success" : progressPct >= 50 ? "bg-warning" : "bg-danger"}`}
                                        role="progressbar"
                                        style={{ width: `${progressPct}%` }}
                                      />
                                    </div>
                                    <small style={{ whiteSpace: "nowrap" }}>
                                      {summary.completed}/{summary.total}
                                    </small>
                                  </div>
                                </td>
                                <td>
                                  <span className={ACCEPTANCE_BADGE[status] || "custom-badge"}>
                                    {ACCEPTANCE_LABELS[status] || status}
                                  </span>
                                </td>
                                <td>
                                  <Link to={`/surgery/${proc.id}`} className="btn btn-sm btn-outline-primary" aria-label="Ver detalle">
                                    <FeatherIcon icon="eye" size={14} />
                                  </Link>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const SurgeonDashboardPage: React.FC = () => <SurgeonDashboard />;

export default SurgeonDashboardPage;
